import { mapSchemaLeaves } from '../mapSchemaLeaves';

/**
 * Prefills the employee form's Position select
 * (`Post_Hire_Data.Positions_Reference` on Workday), using the same
 * `default_value` mechanism as `jobProfilePrefill`.
 *
 * `staffing_entity_id` is Kombo's own id and is exactly the value that field's
 * options carry — the form's option list and the backend lookup read the same
 * Kombo collection. Workday's `position_wid` is never used; the form rejects it.
 *
 * No option is fabricated here: if the id is outside the embedded first page,
 * the shared `seedDefaultValueOptions` in formConfigUtils handles it. The
 * submitted value is correct either way.
 */

interface SchemaField {
  type?: string;
  default_value?: unknown;
  properties?: Record<string, SchemaField>;
  options?: {
    entries?: { id?: string; label?: string; name?: string }[];
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

type Schema = Record<string, SchemaField>;

/**
 * Kombo names this field per HRIS (`Position_Reference` on Workday, plainer
 * elsewhere), so it is matched on shape rather than an exact string — against
 * the label as well as the key, since Kombo is not consistent about which one
 * carries the human name.
 */
const POSITION_TEXT = /position|staffing[\s_-]?entity/i;

/** Neighbours that mention "position" but are not it. */
const NOT_POSITION = /time[\s_-]?type|posting|title/i;

const isSelect = (field: SchemaField): boolean =>
  field?.type === 'single_select' || field?.type === 'multi_select';

const isPositionField = (key: string, field: SchemaField): boolean => {
  if (!isSelect(field)) return false;
  const label = String(field?.label ?? '');
  // A neighbour named on either side disqualifies the field: `Position Time
  // Type` says "position" in both its key and its label, and is not it.
  if (NOT_POSITION.test(key) || NOT_POSITION.test(label)) return false;
  return POSITION_TEXT.test(key) || POSITION_TEXT.test(label);
};

/**
 * Every select in the schema, as `key (label)`. Used to report what was
 * available when no position field matched — the field name varies by Kombo
 * connector, so a miss should say what it saw rather than fail silently.
 */
export const listSelectFields = (schema: Schema | undefined): string[] => {
  if (!schema) return [];
  return Object.entries(schema).flatMap(([key, field]) => {
    if (!field || typeof field !== 'object') return [];
    if (field.type === 'object' && field.properties)
      return listSelectFields(field.properties);
    return isSelect(field) ? [`${key} (${field.label ?? ''})`] : [];
  });
};

const hasOwnDefault = (field: SchemaField): boolean =>
  field.default_value !== undefined && field.default_value !== null;

/**
 * True when the form has a position select still waiting for a value. Callers
 * use it to skip the whole pass on the forms (every non-Workday HRIS) that have
 * no such field.
 */
export const needsPositionPrefill = (schema: Schema | undefined): boolean => {
  if (!schema) return false;

  return Object.entries(schema).some(([key, field]) => {
    if (!field || typeof field !== 'object') return false;
    if (field.type === 'object' && field.properties)
      return needsPositionPrefill(field.properties);
    return isPositionField(key, field) && !hasOwnDefault(field);
  });
};

interface PositionDefaults {
  /** Kombo's id — exactly the value the field's option list uses. */
  staffingEntityId?: string | null;
  /** Workday's readable id (`P-5061`). Display only, never a form value. */
  positionId?: string | null;
}

/**
 * Untouched when there is no id, no position field, or Kombo already supplied a
 * default — that one came from the HRIS and is better evidence. Only branches on
 * the path to a changed field are cloned.
 */
export const withPositionDefault = (
  schema: Schema | undefined,
  defaults: PositionDefaults
): Schema | undefined => {
  const { staffingEntityId } = defaults;
  if (!schema || !staffingEntityId) return schema;

  // Cast back to this module's own Schema: the shared walker is deliberately
  // loose about leaf shape, and callers keep their own.
  return mapSchemaLeaves(schema, (key, field) =>
    isPositionField(key, field as never) && !hasOwnDefault(field as never)
      ? { default_value: staffingEntityId }
      : undefined
  ) as Schema | undefined;
};

interface ApplicationLike {
  workday_position?: {
    staffing_entity_id?: string | null;
    position_id?: string | null;
    status?: string;
  } | null;
}

/**
 * Reads the position defaults off an application, or undefined when it has no
 * synced position — which is every application on every other HRIS.
 */
export const getPositionDefaults = (
  application: ApplicationLike | undefined
): PositionDefaults | undefined => {
  const position = application?.workday_position;
  if (!position?.staffing_entity_id) return undefined;
  return {
    staffingEntityId: position.staffing_entity_id,
    positionId: position.position_id,
  };
};
